import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { seasonEventAPI, scheduledEmailTypeAPI } from '../services/api';

/**
 * EmailSchedulePanel - Dashboard panel for monitoring scheduled email notifications
 * Shows upcoming scheduled emails, sent history, and cron service status
 */
const EmailSchedulePanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [triggeringNotification, setTriggeringNotification] = useState(null);
  const [runningCheck, setRunningCheck] = useState(false);

  // Preview modal state
  const [previewModal, setPreviewModal] = useState({ open: false, loading: false, data: null });
  const [skippingNotification, setSkippingNotification] = useState(null);
  const [unskippingNotification, setUnskippingNotification] = useState(null);


  // Notification type display names
  const notificationTypeLabels = {
    proshop30Day: '30-Day Pro Shop',
    memberOpen: 'Member Open',
    memberReminder: 'Member Reminder',
    proshop8Day: '8-Day Pro Shop',
    proshop3Day: '3-Day Pro Shop'
  };

  // Fetch all data from the new ScheduledNotification collection
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [notificationsRes, historyRes, statusRes] = await Promise.all([
        scheduledEmailTypeAPI.getAllNotifications({ status: ['pending', 'skipped'], sort: 'scheduledFor', order: 'asc' }),
        seasonEventAPI.getNotificationHistory({ limit: 20 }),
        seasonEventAPI.getCronStatus()
      ]);

      // Transform notifications to expected format
      const notifications = (notificationsRes.data.notifications || []).map(notif => ({
        _id: notif._id,
        eventId: notif.eventId,
        eventName: notif.eventName || 'Unknown Event',
        eventDate: notif.eventDate,
        type: notif.emailTypeCode,
        scheduledDate: new Date(notif.scheduledFor),
        label: notificationTypeLabels[notif.emailTypeCode] || notif.emailTypeCode,
        skipped: notif.status === 'skipped',
        skippedReason: notif.skipReason || null,
        status: notif.status
      }));

      // Sort with skipped ones at the end
      notifications.sort((a, b) => {
        if (a.skipped !== b.skipped) return a.skipped ? 1 : -1;
        return a.scheduledDate - b.scheduledDate;
      });

      setPendingNotifications(notifications);
      setNotificationHistory(historyRes.data.notifications || []);
      setCronStatus(statusRes.data);
    } catch (err) {
      console.error('Error fetching email schedule data:', err);
      setError(err.message || 'Failed to load email schedule data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);


  // Trigger a notification manually (still uses eventId + type for legacy compatibility)
  const handleTriggerNotification = async (notificationId, eventId, notificationType) => {
    try {
      setTriggeringNotification(notificationId);
      await seasonEventAPI.triggerNotification(eventId, notificationType);
      await fetchData();
    } catch (err) {
      console.error('Error triggering notification:', err);
      setError(err.message || 'Failed to trigger notification');
    } finally {
      setTriggeringNotification(null);
    }
  };

  // Run daily check manually
  const handleRunDailyCheck = async () => {
    try {
      setRunningCheck(true);
      await seasonEventAPI.runDailyCheck();
      await fetchData();
    } catch (err) {
      console.error('Error running daily check:', err);
      setError(err.message || 'Failed to run daily check');
    } finally {
      setRunningCheck(false);
    }
  };

  // Preview a notification email
  const handlePreviewNotification = async (eventId, notificationType) => {
    try {
      setPreviewModal({ open: true, loading: true, data: null });
      const response = await seasonEventAPI.previewNotification(eventId, notificationType);
      setPreviewModal({ open: true, loading: false, data: response.data.preview });
    } catch (err) {
      console.error('Error previewing notification:', err);
      setError(err.message || 'Failed to preview notification');
      setPreviewModal({ open: false, loading: false, data: null });
    }
  };

  // Skip a scheduled notification (uses new ScheduledNotification system)
  const handleSkipNotification = async (notificationId) => {
    try {
      setSkippingNotification(notificationId);
      await scheduledEmailTypeAPI.skipNotification(notificationId, 'Manually skipped by user');
      await fetchData();
    } catch (err) {
      console.error('Error skipping notification:', err);
      setError(err.message || 'Failed to skip notification');
    } finally {
      setSkippingNotification(null);
    }
  };

  // Re-enable a skipped notification (uses new ScheduledNotification system)
  const handleUnskipNotification = async (notificationId) => {
    try {
      setUnskippingNotification(notificationId);
      await scheduledEmailTypeAPI.unskipNotification(notificationId);
      await fetchData();
    } catch (err) {
      console.error('Error re-enabling notification:', err);
      setError(err.message || 'Failed to re-enable notification');
    } finally {
      setUnskippingNotification(null);
    }
  };

  // Close preview modal
  const closePreviewModal = () => {
    setPreviewModal({ open: false, loading: false, data: null });
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const target = new Date(date);
    const diffMs = target - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `in ${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `in ${diffHours}h`;
    } else if (diffMs > 0) {
      return 'soon';
    } else {
      return 'overdue';
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      sent: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      skipped: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Card className="col-span-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">📧</span>
            Email Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with status and actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <span className="text-xl">📧</span>
              Email Schedule Monitor
            </CardTitle>
            <div className="flex items-center gap-2">
              {cronStatus && (
                <Badge className={cronStatus.cronService?.isRunning ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {cronStatus.cronService?.isRunning ? '🟢 Cron Active' : '🔴 Cron Stopped'}
                </Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleRunDailyCheck}
                disabled={runningCheck}
              >
                {runningCheck ? 'Checking...' : 'Run Check Now'}
              </Button>
              <Button size="sm" variant="outline" onClick={fetchData}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        {error && (
          <CardContent className="pt-0">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
              {error}
            </div>
          </CardContent>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upcoming Scheduled Emails */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>🗓️</span>
              Upcoming Scheduled Emails
              <Badge variant="outline" className="ml-auto">
                {pendingNotifications.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingNotifications.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <p>No upcoming scheduled emails</p>
                <p className="text-sm mt-1">Create season events to schedule notifications</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pendingNotifications.slice(0, 10).map((notif, idx) => (
                  <div
                    key={`${notif.eventId}-${notif.type}-${idx}`}
                    className={`border rounded-lg p-3 transition-colors ${
                      notif.skipped
                        ? 'bg-gray-50 border-gray-200 opacity-60'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm truncate ${notif.skipped ? 'line-through text-gray-500' : ''}`}>
                          {notif.eventName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Event: {formatDate(notif.eventDate)}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {notif.label}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatDate(notif.scheduledDate)}
                          </span>
                          {notif.skipped && (
                            <Badge className="text-xs bg-gray-100 text-gray-600">
                              Skipped
                            </Badge>
                          )}
                        </div>
                        {notif.skipped && notif.skippedReason && (
                          <p className="text-xs text-gray-400 mt-1 italic">
                            {notif.skippedReason}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {!notif.skipped && (
                          <span className="text-xs font-medium text-blue-600">
                            {formatRelativeTime(notif.scheduledDate)}
                          </span>
                        )}
                        <div className="flex items-center gap-1">
                          {notif.skipped ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs"
                              onClick={() => handleUnskipNotification(notif._id)}
                              disabled={unskippingNotification === notif._id}
                            >
                              {unskippingNotification === notif._id
                                ? 'Enabling...'
                                : 'Re-enable'}
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs text-blue-600 hover:text-blue-800"
                                onClick={() => handlePreviewNotification(notif.eventId, notif.type)}
                              >
                                Preview
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                onClick={() => handleSkipNotification(notif._id)}
                                disabled={skippingNotification === notif._id}
                              >
                                {skippingNotification === notif._id
                                  ? 'Skipping...'
                                  : 'Skip'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs"
                                onClick={() => handleTriggerNotification(notif._id, notif.eventId, notif.type)}
                                disabled={triggeringNotification === notif._id}
                              >
                                {triggeringNotification === notif._id
                                  ? 'Sending...'
                                  : 'Send Now'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>📬</span>
              Recent Email Activity
              <Badge variant="outline" className="ml-auto">
                {notificationHistory.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notificationHistory.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <p>No notification history yet</p>
                <p className="text-sm mt-1">Sent emails will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notificationHistory.map((notif) => (
                  <div
                    key={notif._id}
                    className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {notif.seasonEventId?.name || 'Unknown Event'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {notificationTypeLabels[notif.notificationType] || notif.notificationType}
                          </Badge>
                          <Badge className={`text-xs ${getStatusBadge(notif.status)}`}>
                            {notif.status}
                          </Badge>
                        </div>
                        {notif.error && (
                          <p className="text-xs text-red-500 mt-1 truncate">
                            Error: {notif.error?.message || String(notif.error)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-500">
                          {formatDate(notif.createdAt)}
                        </span>
                        {notif.recipientCount > 0 && (
                          <p className="text-xs text-gray-500">
                            {notif.recipientCount} recipients
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      {cronStatus && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>⚙️</span>
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <p className="text-sm opacity-90">Cron Service</p>
                <p className="text-lg font-bold">
                  {cronStatus.cronService?.isRunning ? 'Running' : 'Stopped'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                <p className="text-sm opacity-90">Jobs Scheduled</p>
                <p className="text-lg font-bold">
                  {cronStatus.cronService?.jobCount || 0}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
                <p className="text-sm opacity-90">Last Check</p>
                <p className="text-lg font-bold">
                  {cronStatus.proshopService?.lastRunAt
                    ? formatDate(cronStatus.proshopService.lastRunAt)
                    : 'Never'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-4 text-white">
                <p className="text-sm opacity-90">Total Sent</p>
                <p className="text-lg font-bold">
                  {cronStatus.proshopService?.totalNotificationsSent || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Preview Modal */}
      {previewModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closePreviewModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span>📧</span>
                Email Preview
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closePreviewModal}
                className="h-8 w-8 p-0"
              >
                ✕
              </Button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4">
              {previewModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                    <p className="text-gray-500">Loading email preview...</p>
                  </div>
                </div>
              ) : previewModal.data ? (
                <div className="space-y-4">
                  {/* Email Metadata */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-20">To:</span>
                      <span className="text-sm text-gray-900">
                        {previewModal.data.recipients?.join(', ') || 'No recipients'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-20">Subject:</span>
                      <span className="text-sm text-gray-900 font-medium">
                        {previewModal.data.subject || 'No subject'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-20">Event:</span>
                      <span className="text-sm text-gray-900">
                        {previewModal.data.eventName || 'Unknown event'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-20">Type:</span>
                      <Badge variant="outline" className="text-xs">
                        {notificationTypeLabels[previewModal.data.notificationType] || previewModal.data.notificationType}
                      </Badge>
                    </div>
                  </div>

                  {/* Email HTML Preview */}
                  <div className="border rounded-lg">
                    <div className="bg-gray-100 px-4 py-2 border-b">
                      <span className="text-xs font-medium text-gray-500 uppercase">Email Body</span>
                    </div>
                    <div
                      className="p-4 bg-white"
                      dangerouslySetInnerHTML={{ __html: previewModal.data.body || '<p>No content</p>' }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>No preview data available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
              <Button variant="outline" onClick={closePreviewModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmailSchedulePanel;
